import { useMemo } from "react";
import {
  Briefcase, Home, ChevronDown, Sparkles, ClipboardList, CalendarDays,
  MessageSquare, UserCircle2,
} from "lucide-react";
import {
  BRAND_COLORS, BOOKING_PILL, STATUS_COLORS,
  effectiveBookingStatus, taskStatusColor, withAlpha,
} from "../../lib/bookingStatus";
import { DE_DATE } from "../../lib/dateFormat";

/**
 * Feb 2026 · User-Directive „Alle Einträge für dieses Objekt" (1 Sep 2026).
 *
 * Vereinheitlichte, chronologische Liste (neueste zuerst) aller Einträge
 * eines Objekts:
 *   • Abwesenheiten (`bookings`)
 *   • Extraleistungen (`service_requests` mit source=extras_bulk)
 *   • Request-Anything (`service_requests` mit source=request_anything)
 *   • Vom Team erfasste Aufgaben (`service_requests` mit source=team_captured)
 *   • Buchungs-Änderungen (`service_requests` mit source=booking_change)
 *   • Vom Admin im Kalender angelegte Termine (`manual_events`)
 *
 * Jede Zeile ist klickbar. `onOpen(type, entry)` bekommt den Zeilen-Typ
 * ("booking" | "request" | "event") und die Roh-Entität — die Elternseite
 * entscheidet, welches Modal geöffnet wird.
 *
 * Ersetzt die frühere `BookingsListSection` in der Objekt-Detailansicht;
 * das Kunden-Detail nutzt weiterhin die alte, buchungsschmale Liste.
 */

const SOURCE_LABEL = {
  extras_bulk: "Extraleistung",
  request_anything: "Anfrage",
  team_captured: "Team-Aufgabe",
  booking_change: "Buchungs-Änderung",
};

const KIND_LABEL = {
  task: "Termin",
  inspection: "Kontrolle",
  reminder: "Erinnerung",
};

function statusPillFor(row) {
  if (row.__type === "booking") {
    const key = effectiveBookingStatus(row) || "requested";
    return BOOKING_PILL[key] || BOOKING_PILL.requested;
  }
  if (row.__type === "request") {
    const st = row.status || "new";
    // Cancelled bleibt rot-diagonal (visuell im Row-Border), Pill-Text bleibt.
    const color = taskStatusColor(st) || STATUS_COLORS.grey;
    return {
      bg: color,
      label:
        st === "completed" ? "Erledigt"
          : st === "cancelled" ? "Storniert"
          : st === "in_progress" ? "In Arbeit"
          : "Neu",
    };
  }
  // manual event
  const done = !!row.done;
  return {
    bg: done ? STATUS_COLORS.green : STATUS_COLORS.blue,
    label: done ? "Erledigt" : "Offen",
  };
}

function iconFor(row) {
  if (row.__type === "booking") return Home;
  if (row.__type === "event") return CalendarDays;
  const src = row.source;
  if (src === "extras_bulk") return Sparkles;
  if (src === "request_anything") return MessageSquare;
  return ClipboardList;
}

function sortDateFor(row) {
  if (row.__type === "booking") return row.from_date || row.created_at || "";
  if (row.__type === "event") return row.start_date || row.created_at || "";
  return row.date_needed || row.created_at || "";
}

function titleFor(row) {
  if (row.__type === "booking") {
    return `${DE_DATE(row.from_date)} → ${DE_DATE(row.to_date)}`;
  }
  if (row.__type === "event") return row.title || KIND_LABEL[row.kind] || "Termin";
  return row.title || "Anfrage";
}

function subtitleFor(row) {
  if (row.__type === "booking") {
    const bits = [];
    if (row.property_name) bits.push(row.property_name);
    if ((row.services?.length || 0) > 0) {
      const n = row.services.length;
      bits.push(`${n} Zusatzleistung${n === 1 ? "" : "en"}`);
    }
    return bits.join(" · ");
  }
  if (row.__type === "event") {
    const bits = [];
    if (row.start_date && row.end_date && row.start_date !== row.end_date) {
      bits.push(`${DE_DATE(row.start_date)} → ${DE_DATE(row.end_date)}`);
    } else if (row.start_date) {
      bits.push(DE_DATE(row.start_date));
    }
    if (KIND_LABEL[row.kind]) bits.push(KIND_LABEL[row.kind]);
    return bits.join(" · ");
  }
  const bits = [];
  if (row.date_needed) bits.push(DE_DATE(row.date_needed));
  if (SOURCE_LABEL[row.source]) bits.push(SOURCE_LABEL[row.source]);
  return bits.join(" · ");
}

function assigneeChipFor(row) {
  // Feb 2026 · Directive „Concierge-Name inline" (1 Sep 2026):
  // Backend liefert für Bookings + Requests + Events `assigned_to_name`
  // + `assigned_to_role`. Wenn beides fehlt, sagen wir es explizit —
  // sonst wüsste der Admin nicht, dass die Zeile unassigned ist.
  const name = row.assigned_to_name;
  const role = row.assigned_to_role;
  if (!name) return { text: "Nicht zugewiesen", muted: true };
  const roleLabel = role === "admin" ? "Admin" : "Concierge";
  return { text: `${name} · ${roleLabel}`, muted: false };
}

export function PropertyEntriesSection({
  propertyId,
  bookings,
  requests,
  events,
  onOpen,
}) {
  const rows = useMemo(() => {
    const filtered = [];
    for (const b of bookings || []) {
      if (b.property_id !== propertyId) continue;
      filtered.push({ ...b, __type: "booking" });
    }
    for (const r of requests || []) {
      if (r.property_id !== propertyId) continue;
      filtered.push({ ...r, __type: "request" });
    }
    for (const e of events || []) {
      if (e.property_id !== propertyId) continue;
      filtered.push({ ...e, __type: "event" });
    }
    filtered.sort((a, b) => (sortDateFor(b) || "").localeCompare(sortDateFor(a) || ""));
    return filtered;
  }, [propertyId, bookings, requests, events]);

  if (rows.length === 0) return null;
  return (
    <div className="bg-white border border-navy/10 p-5 md:p-6" data-testid="property-entries-list">
      <div className="flex items-center gap-2 mb-4">
        <Briefcase className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.4} />
        <div className="flex-1 min-w-0">
          <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>Alle Einträge für dieses Objekt</div>
          <div className="text-[12.5px] text-navy-dark/60">
            Abwesenheiten, Extraleistungen, Aufgaben und Kalender-Termine — chronologisch, neueste zuerst.
          </div>
        </div>
        <div className="text-[10.5px] tracking-widest uppercase text-navy-dark/50 shrink-0">
          {rows.length}
        </div>
      </div>
      <ul className="space-y-1.5">
        {rows.map((row) => {
          const pill = statusPillFor(row);
          const Icon = iconFor(row);
          const isCancelled = row.__type === "booking"
            ? (effectiveBookingStatus(row) === "cancelled")
            : row.status === "cancelled";
          const stripe = isCancelled
            ? `repeating-linear-gradient(45deg, ${withAlpha(STATUS_COLORS.red, 0.06)} 0, ${withAlpha(STATUS_COLORS.red, 0.06)} 6px, transparent 6px, transparent 12px)`
            : undefined;
          return (
            <li key={`${row.__type}-${row.id || row.booking_id}`}>
              <button
                type="button"
                onClick={() => onOpen?.(row.__type, row)}
                data-testid={`property-entry-${row.__type}-${row.id || row.booking_id}`}
                data-status={pill.label}
                className="w-full flex items-center gap-3 p-3 md:p-4 border border-navy/10 bg-white hover:border-gold transition-colors text-left"
                style={stripe ? { background: stripe } : undefined}
              >
                <span
                  className="w-6 h-6 shrink-0 flex items-center justify-center"
                  style={{ background: withAlpha(pill.bg, 0.12), color: pill.bg }}
                  aria-hidden
                >
                  <Icon className="w-3.5 h-3.5" strokeWidth={1.7} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] text-navy leading-snug truncate">
                    {titleFor(row)}
                  </div>
                  <div className="text-[11.5px] text-navy-dark/60 mt-0.5 truncate">
                    {subtitleFor(row)}
                  </div>
                  {(() => {
                    const chip = assigneeChipFor(row);
                    return (
                      <div
                        className={`mt-1 inline-flex items-center gap-1 text-[11px] ${chip.muted ? "text-navy-dark/50 italic" : "text-navy-dark/75"}`}
                        data-testid={`property-entry-assignee-${row.__type}-${row.id || row.booking_id}`}
                      >
                        <UserCircle2 className="w-3 h-3" strokeWidth={1.7} />
                        <span className="truncate">{chip.text}</span>
                      </div>
                    );
                  })()}
                </div>
                <span
                  className="text-[10px] tracking-[0.14em] uppercase px-2.5 py-1 shrink-0"
                  style={{ background: withAlpha(pill.bg, 0.12), color: pill.bg }}
                >
                  {pill.label}
                </span>
                <ChevronDown className="w-3.5 h-3.5 shrink-0 -rotate-90 text-navy-dark/40" strokeWidth={2} />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
